import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';
import * as echarts from 'echarts';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { formatStat } from '@shared-lib/utils/format.utils';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { ResponsiveLayoutService } from '@shared-lib/shared-services/responsive-layout.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { buildCategoryFilterData, buildLegendSyncData, matchValue, MatchMode } from '@shared-lib/utils/filter.utils';

@Component({
  selector: 'app-mixed-stackbar-chart',
  imports: [CommonModule],
  templateUrl: './mixed-stackbar-chart.component.html',
  styleUrl: './mixed-stackbar-chart.component.scss',
})
export class MixedStackbarChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any; // widget config containing widgetId, apiUrl, title, etc.
  private myChart!: echarts.ECharts;
  private resizeObserver!: ResizeObserver;
  protected responsiveLayoutService = inject(ResponsiveLayoutService);
  protected reflowSub?: Subscription;

  @ViewChild('legendContainer')
  legendContainerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('chartContainer')
  chartContainerRef!: ElementRef<HTMLDivElement>;

  // Scroll / pagination state
  scrollOffset = 0;
  maxVisibleCategories = 7;
  visibleCategories: string[] = [];
  visibleWidth = 600;
  // thumb drag state
  private isScrollbarDragging = false;
  private scrollbarStartX = 0;
  private scrollStartOffset = 0;
  private dragHandler: ((e: MouseEvent) => void) | null = null;
  private upHandler: (() => void) | null = null;

  // store all categories for scrollbar math
  protected allCategories: string[] = [];
  // preserve last loaded datasets so re-render during scroll works
  private lastBarChartData?: { category: string[]; series: any[] };
  private lastLineChartData?: { category: string[]; series: any[] };
  private lastRawBarRows?: any[];
  private lastRawComputeRows?: any[];

  legendItems: { name: string; color: string }[] = [];
  showLeftArrow = false;
  showRightArrow = false;

  protected unSubscribe$ = new Subject<void>();
  protected filterSub!: Subscription;
  protected selectedSeriesName: string | null = null;
  protected originalOption: any = null;
  protected lastAppliedHeight: string | null = null;
  selectedCategories: string[] = [];
  selectedLegends: string[] = [];

  @HostListener('window:resize')
  onResize() {
    this.myChart?.resize();
    this.updateArrowVisibility();
  }

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
    protected chartFilterHandler: ChartFilterHandlerService,
  ) { }

  ngAfterViewInit(): void {
    // Initial load
    setTimeout(() => this.loadAndRenderChart(), 0);

    this.resizeObserver = new ResizeObserver(() => {
      this.myChart?.resize();
    });

    this.filterSub = this.globalFilterService.filter$
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((filters) => {
        if (!this.chartFilterHandler.shouldReload(filters, this.data?.widgetId)) {
          return;
        }

        // Handle legendSync (visual dimming from other charts)
        if (filters?.legendSync) {
          this.handleExternalLegendSync(filters);
          if (filters.isCategoryFilter) {
            this.loadAndRenderChart(filters);
          }
          return;
        }

        // Only clear selection on global dropdown change (no widgetId)
        if (!filters?.widgetId) {
          this.selectedCategories = [];
          this.selectedLegends = [];
        }

        this.selectedSeriesName = null;
        this.loadAndRenderChart(filters);
      });
    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    // Update arrow visibility after view is ready
    setTimeout(() => this.updateArrowVisibility(), 200);
  }

  protected loadAndRenderChart(filters?: any): void {
    console.log('Loading chart for widget', this.data?.widgetId, 'with filters', filters);
    const chartDom = document.getElementById(this.data?.widgetId || '');
    console.log('chartDom:', chartDom, 'widgetId:', this.data?.widgetId);
    if (!chartDom) {
      console.warn('Chart DOM not found:', this.data?.widgetId);
      return;
    }

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
      this.resizeObserver.observe(chartDom);
    }

    this.myChart.showLoading();

    if (this.data?.data) {
      this.lastBarChartData = this.data.data;
      this.lastLineChartData = undefined;
      this.initChart(this.lastBarChartData, this.lastLineChartData);
    } else if (this.data?.apiUrl) {
      // Fetch from API or local JSON
      console.log('Making API call to', this.data.apiUrl);
      this.configService.getConfig(this.data.apiUrl, this.data, filters).subscribe({
        next: (res) => {
          console.log('API response for widget', this.data?.widgetId, res);
          let barChartData, lineChartData;
          if (
            res?.responseList &&
            res.responseList.length > 0 &&
            res.responseList[0].data &&
            res.responseList[0].data.length > 0
          ) {
            const dataArray = res.responseList[0].data;
            console.log('dataArray:', dataArray);
            console.log('dataArray[0]:', dataArray[0]);
            if (dataArray[0]?.bar) {
              barChartData = this.transformBarData(
                dataArray[0].bar,
                this.data.categoryKey,
                this.data.seriesKey,
                this.data.valueKey,
                
              );
              this.lastRawBarRows = dataArray[0].bar;
              this.lastRawComputeRows = dataArray[0].compute || dataArray[0].computed;
              console.log('barChartData:', barChartData);
              // If lineValueKey is provided but line values live on bar entries,
              // derive line series by aggregating per category (average).
              if (this.data.lineValueKey) {
                // Use barChartData.category to ensure category ordering matches
                lineChartData = this.transformLineDataFromBar(
                  dataArray[0].bar,
                  this.data.categoryKey,
                  this.data.lineValueKey,
                  (barChartData && barChartData.category) || undefined,
                );
                console.log('derived lineChartData from bar:', lineChartData);
              }
            }
            if ((dataArray[0]?.computed || dataArray[0]?.compute) && this.data.lineValueKey) {
              const computedSource = dataArray[0].computed || dataArray[0].compute;
              lineChartData = this.transformLineData(
                computedSource,
                this.data.categoryKey,
                this.data.lineValueKey,
              );
              console.log('lineChartData:', lineChartData);
            }
          } else if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
            const dataArray = res.data;
            console.log('dataArray from res.data:', dataArray);
            if (dataArray[0]?.bar) {
              barChartData = this.transformBarData(
                dataArray[0].bar,
                this.data.categoryKey,
                this.data.seriesKey,
                this.data.valueKey,
              );
              this.lastRawBarRows = dataArray[0].bar;
              this.lastRawComputeRows = dataArray[0].compute || dataArray[0].computed;
              console.log('barChartData:', barChartData);
              if (this.data.lineValueKey) {
                lineChartData = this.transformLineDataFromBar(
                  dataArray[0].bar,
                  this.data.categoryKey,
                  this.data.lineValueKey,
                  (barChartData && barChartData.category) || undefined,
                );
                console.log('derived lineChartData from res.data[0].bar:', lineChartData);
              }
              if (this.data.lineValueKey) {
                lineChartData = this.transformLineDataFromBar(
                  dataArray[0].bar,
                  this.data.categoryKey,
                  this.data.lineValueKey,
                  (barChartData && barChartData.category) || undefined,
                );
                console.log('derived lineChartData from res.data.bar:', lineChartData);
              }
            }
            if ((dataArray[0]?.computed || dataArray[0]?.compute) && this.data.lineValueKey) {
              const computedSource = dataArray[0].computed || dataArray[0].compute;
              lineChartData = this.transformLineData(
                computedSource,
                this.data.categoryKey,
                this.data.lineValueKey,
              );
              console.log('lineChartData:', lineChartData);
            }
          } else if (res?.bar) {
            barChartData = this.transformBarData(
              res.bar,
              this.data.categoryKey,
              this.data.seriesKey,
              this.data.valueKey,
            );
            this.lastRawBarRows = res.bar;
            this.lastRawComputeRows = res.compute || res.computed;
            console.log('barChartData from res.bar:', barChartData);
            if (this.data.lineValueKey) {
              lineChartData = this.transformLineDataFromBar(
                res.bar,
                this.data.categoryKey,
                this.data.lineValueKey,
                (barChartData && barChartData.category) || undefined,
              );
              console.log('derived lineChartData from res.bar:', lineChartData);
            }
            if ((res?.computed || res?.compute) && this.data.lineValueKey) {
              const computedSource = res.computed || res.compute;
              lineChartData = this.transformLineData(
                computedSource,
                this.data.categoryKey,
                this.data.lineValueKey,
              );
              console.log('lineChartData from res.computed/compute:', lineChartData);
            }
          } else if (res?.computed && this.data.lineValueKey) {
            lineChartData = this.transformLineData(
              res.computed,
              this.data.categoryKey,
              this.data.lineValueKey,
            );
            console.log('lineChartData from res.computed:', lineChartData);
          } else if (res?.data) {
            barChartData = res.data;
          } else {
            barChartData = res;
          }
          this.lastBarChartData = barChartData;
          this.lastLineChartData = lineChartData;
          this.initChart(this.lastBarChartData, this.lastLineChartData);
        },
        error: (err) => {
          console.error('API error for widget', this.data?.widgetId, err);
          this.myChart.hideLoading();
        },
      });
    } else {
      this.myChart.hideLoading();
      console.warn('No data or apiUrl provided for widget:', this.data?.widgetId);
    }

    this.addResizeListener();
  }

  protected initChart(
    barChartData?: { category: string[]; series: any[] },
    lineChartData?: { category: string[]; series: any[] },
  ): void {
    console.log(
      'initChart called with barChartData:',
      barChartData,
      'lineChartData:',
      lineChartData,
    );
    const labelPrefix = this.data?.labelPrefix || '';

    // Determine x-axis label rotation:
    // - `chartConfig.xLabelVertical: true` or `chartConfig.xLabelOrientation === 'vertical'` => 90deg
    // - `chartConfig.xLabelRotate` (number or numeric-string) => that value
    // - default => 0
    const cfg = this.data?.chartConfig || {};
    let xLabelRotate = 0;
    if (cfg.xLabelVertical === true || cfg.xLabelOrientation === 'vertical') {
      xLabelRotate = 90;
    } else if (typeof cfg.xLabelRotate === 'number') {
      xLabelRotate = cfg.xLabelRotate;
    } else if (typeof cfg.xLabelRotate === 'string' && !Number.isNaN(Number(cfg.xLabelRotate))) {
      xLabelRotate = Number(cfg.xLabelRotate);
    }

    const defaultPalette = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'];
    const colors = this.data?.colors || defaultPalette;
    const topNColors: string[] = this.data?.chartConfig?.topNColorsByStackPosition || [];
    const colorMap: { [k: string]: string } = this.data?.chartConfig?.colorMap || {};
    const stackOrder = this.data?.stackOrder || 'asc';

    const getSeriesColor = (seriesName: string, idx: number): string => {
      // Color consistency: reuse same color for same series name across all charts
      if (this.data?.enableColorConsistency) {
        const globalColor = this.chartFilterHandler.getItemColor(seriesName);
        if (globalColor) return globalColor;
      }

      let color: string;
      if (topNColors && topNColors.length > idx && topNColors[idx]) {
        color = topNColors[idx];
      } else if (colorMap && colorMap[seriesName]) {
        color = colorMap[seriesName];
      } else {
        color = colors[idx % colors.length];
      }

      // Register color so other charts can use the same color for this series name
      if (this.data?.enableColorConsistency && color) {
        this.chartFilterHandler.registerItemColor(seriesName, color);
      }

      return color;
    };

    const allCategories = new Set([
      ...(barChartData?.category || []),
      ...(lineChartData?.category || []),
    ]);
    const categories = Array.from(allCategories).sort();
    this.allCategories = categories;

    // Optional double-axis (multi-level) support
    // When enabled, rebuild categories and bar series from raw rows (`lastRawBarRows`)
    // using configured outer/inner keys so we can render two category axes.
    let outerAxisHeaderLabels: string[] | undefined;
    let outerBoundaries: { name: string; startIdx: number; endIdx: number }[] | undefined;
    let flatInners: string[] | undefined;
    let flatOuters: string[] | undefined;
    let _doubleOuterKey: string | undefined;
    let _doubleInnerKey: string | undefined;
    // Prepare barSeriesArray variable early so double-axis path can assign to it
    let barSeriesArray: any[] | undefined;
    const doubleAxisActive = Boolean(this.data?.chartConfig?.doubleAxis || this.data?.doubleAxis);
    if (
      doubleAxisActive &&
      this.lastRawBarRows &&
      this.lastRawBarRows.length &&
      (this.data?.chartConfig?.doubleOuterKey || this.data?.doubleOuterKey) &&
      (this.data?.chartConfig?.doubleInnerKey || this.data?.doubleInnerKey) &&
      this.data?.seriesKey &&
      this.data?.valueKey && this.data?.lineValueKey
    ) {
      const outerKey = this.data?.doubleOuterKey || this.data?.chartConfig?.doubleOuterKey;
      const innerKey = this.data?.doubleInnerKey || this.data?.chartConfig?.doubleInnerKey;
      _doubleOuterKey = outerKey;
      _doubleInnerKey = innerKey;
      const valueKey = this.data.valueKey;
      const seriesKey = this.data.seriesKey;
      const lineValueKey = this.data.lineValueKey;

      const outerOrder: string[] = [];
      const outerInnerMap = new Map<string, string[]>();

      for (const row of this.lastRawBarRows) {
        const o = row[outerKey] == null ? '' : String(row[outerKey]);
        const i = row[innerKey] == null ? '' : String(row[innerKey]);
        if (!outerOrder.includes(o)) outerOrder.push(o);
        if (!outerInnerMap.has(o)) outerInnerMap.set(o, []);
        const inners = outerInnerMap.get(o)!;
        if (!inners.includes(i)) inners.push(i);
      }

      // Ensure stable ordering
      outerOrder.forEach((o) => {
        const arr = outerInnerMap.get(o) || [];
        arr.sort();
        outerInnerMap.set(o, arr);
      });

      // Build flat inner labels and corresponding outer lookup
      flatInners = [];
      flatOuters = [];
      outerOrder.forEach((o) => {
        const inners = outerInnerMap.get(o) || [];
        inners.forEach((inn) => {
          flatInners!.push(inn);
          flatOuters!.push(o);
        });
      });

      // Rebuild stacked series from raw rows grouped by seriesKey
      const seriesNames = [...new Set(this.lastRawBarRows.map((r: any) => String(r[seriesKey] || '')))].filter((s) => s !== '');
      const rebuiltSeries = seriesNames.map((sName: string, idx: number) => {
        const dataPoints = flatInners!.map((inn, pos) => {
          const out = flatOuters![pos];
          const items = this.lastRawBarRows!.filter((r: any) => String(r[outerKey]) === out && String(r[innerKey]) === inn && String(r[seriesKey]) === sName);
          return items.reduce((sum: number, it: any) => sum + (Number(it[valueKey]) || 0), 0);
        });
        return {
          name: sName,
          type: 'bar',
          stack: 'total',
          emphasis: { focus: 'none' },
          data: dataPoints,
          yAxisIndex: 0,
          barWidth: this.data?.chartConfig?.barWidth || '90%',
          barMaxWidth: this.data?.chartConfig?.barMaxWidth || 48,
          barCategoryGap: this.data?.chartConfig?.barCategoryGap || '20%',
          itemStyle: { color: getSeriesColor(sName, idx) },
        };
      });

      // Replace computed categories and assign rebuilt series to barSeriesArray
      categories.splice(0, categories.length, ...flatInners);
      this.allCategories = categories;
      barSeriesArray = rebuiltSeries;

      // Build outer axis header labels (place outer name at group's midpoint)
      const boundaries: { name: string; startIdx: number; endIdx: number }[] = [];
      let gStart = 0;
      outerOrder.forEach((o) => {
        const len = (outerInnerMap.get(o) || []).length;
        if (len <= 0) return;
        const startIdx = gStart;
        const endIdx = gStart + len - 1;
        boundaries.push({ name: o, startIdx, endIdx });
        gStart += len;
      });
      outerBoundaries = boundaries;
      outerAxisHeaderLabels = new Array(categories.length).fill('');
      boundaries.forEach((b) => {
        const mid = Math.floor((b.startIdx + b.endIdx) / 2);
        outerAxisHeaderLabels![mid] = b.name;
      });
    }

    if (!barSeriesArray) {
      barSeriesArray =
        barChartData?.series.map((s, i) => ({
          name: s.name,
          type: 'bar',
          stack: 'total',
          emphasis: { focus: 'none' },
          data: categories.map((cat) => s.data[barChartData.category.indexOf(cat)] || 0),
          yAxisIndex: 0,
          barWidth: this.data?.chartConfig?.barWidth || '90%',
          barMaxWidth: this.data?.chartConfig?.barMaxWidth || 48,
          barCategoryGap: this.data?.chartConfig?.barCategoryGap || '20%',
          itemStyle: { color: getSeriesColor(s.name, i) },
        })) || [];
    }

    // Reorder stacked series based on a reference category's values so the topmost
    // segment corresponds to the largest value in that reference bar. The
    // reference category can be selected via `data.chartConfig.sortReference`:
    // - 'first' (default) => use the first category (index 0)
    // - 'last' => use the last category (index categories.length - 1)
    try {
      const sortRef = String(this.data?.chartConfig?.sortReference || 'first').toLowerCase();
      let refIndex = 0;
      if (sortRef === 'last') {
        refIndex = Math.max(0, categories.length - 1);
      } else {
        refIndex = 0;
      }
      const extractNumeric = (val: any) => {
        if (val === null || val === undefined) return 0;
        if (typeof val === 'number') return val;
        if (typeof val === 'string' && !Number.isNaN(Number(val))) return Number(val);
        if (typeof val === 'object') {
          // common shapes: { value: number } or { value: number, ... }
          if ('value' in val && !Number.isNaN(Number((val as any).value))) return Number((val as any).value);
          // fallback: try to coerce to number
          const n = Number(val);
          return Number.isFinite(n) ? n : 0;
        }
        return 0;
      };

      barSeriesArray.sort((a: any, b: any) => {
        const va = extractNumeric(a.data && a.data[refIndex]);
        const vb = extractNumeric(b.data && b.data[refIndex]);
        return va - vb; // ascending so last = largest (topmost)
      });
      const total = barSeriesArray.length;
      barSeriesArray = barSeriesArray.map((s: any, idx: number) => ({
        ...s,
        itemStyle: { color: getSeriesColor(s.name, total - 1 - idx) },
      }));
    } catch (e) {
      // ignore
    }

    // Apply stackOrder: reverse series array if 'asc' to show colors bottom-to-top, keep original if 'desc' for top-to-bottom
    if (stackOrder === 'desc') {
      barSeriesArray = barSeriesArray.reverse();
    }

    const barSeries = barSeriesArray;

    // --- Determine stacked totals and choose a consistent scale (K/M/B) ---
    const extractNumericForScale = (val: any) => {
      if (val === null || val === undefined) return 0;
      if (typeof val === 'number') return val;
      if (typeof val === 'string' && !Number.isNaN(Number(val))) return Number(val);
      if (typeof val === 'object') {
        if ('value' in val && !Number.isNaN(Number((val as any).value))) return Number((val as any).value);
        const n = Number(val);
        return Number.isFinite(n) ? n : 0;
      }
      return 0;
    };

    const categoryTotals: number[] = categories.map((cat, idx) =>
      barSeries.reduce((sum: number, s: any) => sum + extractNumericForScale(s.data[idx]), 0),
    );
    const maxStackTotal = categoryTotals.length ? Math.max(...categoryTotals) : 0;

    let scaleFactor = 1;
    let scaleSuffix = '';
    if (Math.abs(maxStackTotal) >= 1e9) {
      scaleFactor = 1e9;
      scaleSuffix = 'B';
    } else if (Math.abs(maxStackTotal) >= 1e6) {
      scaleFactor = 1e6;
      scaleSuffix = 'M';
    } else if (Math.abs(maxStackTotal) >= 1e3) {
      scaleFactor = 1e3;
      scaleSuffix = 'K';
    }

    const formatScaled = (v: any) => {
      const num = extractNumericForScale(v);
      if (scaleFactor === 1) return formatStat(num, '', labelPrefix);
      const scaled = num / scaleFactor;
      let s = scaled.toFixed(2).replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
      return `${s} ${scaleSuffix}`;
    };

    // --- Determine quantity scale separately so small quantity numbers don't become 0 when value scale is B/M/K ---
    const quantityLabelKey = this.data?.quantityKey || 'quantity';
    const extractNumericForQty = (val: any) => {
      if (val === null || val === undefined) return 0;
      if (typeof val === 'number') return val;
      if (typeof val === 'string' && !Number.isNaN(Number(val))) return Number(val);
      if (typeof val === 'object') {
        if (quantityLabelKey in val && !Number.isNaN(Number((val as any)[quantityLabelKey]))) return Number((val as any)[quantityLabelKey]);
        if ('value' in val && !Number.isNaN(Number((val as any).value))) return Number((val as any).value);
        const n = Number(val);
        return Number.isFinite(n) ? n : 0;
      }
      return 0;
    };

    const categoryQtyTotals: number[] = categories.map((cat, idx) =>
      barSeries.reduce((sum: number, s: any) => sum + extractNumericForQty(s.data[idx]), 0),
    );
    const maxQtyTotal = categoryQtyTotals.length ? Math.max(...categoryQtyTotals) : 0;

    let qtyScaleFactor = 1;
    let qtyScaleSuffix = '';
    if (Math.abs(maxQtyTotal) >= 1e9) {
      qtyScaleFactor = 1e9;
      qtyScaleSuffix = 'B';
    } else if (Math.abs(maxQtyTotal) >= 1e6) {
      qtyScaleFactor = 1e6;
      qtyScaleSuffix = 'M';
    } else if (Math.abs(maxQtyTotal) >= 1e3) {
      qtyScaleFactor = 1e3;
      qtyScaleSuffix = 'K';
    }

    const formatQuantity = (v: any) => {
      const num = extractNumericForQty(v);
      if (qtyScaleFactor === 1) return formatStat(num, '', labelPrefix);
      const scaled = num / qtyScaleFactor;
      let s = scaled.toFixed(2).replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
      return `${s} ${qtyScaleSuffix}`;
    };

    const preferredLineColor = this.data?.lineColor || this.data?.chartConfig?.lineColor || '#F37B75';
    let lineSeries = [] as any[];
    if (
      doubleAxisActive &&
      flatInners &&
      flatOuters &&
      this.lastRawBarRows &&
      this.data?.lineValueKey
    ) {
      const valKey = this.data.lineValueKey;
      const useComputeData = (valKey === 'net_margin_percent' || valKey === 'margin_percent') && this.lastRawComputeRows;
      console.log(`✓ Double-axis line series: valKey="${valKey}", useComputeData=${useComputeData}, computeRows available=${this.lastRawComputeRows?.length || 0}`);
      
      lineSeries = (lineChartData?.series || [{ name: this.data?.lineLabel || valKey }]).map((s: any) => ({
        name: s.name,
        type: 'line',
        data: flatInners!.map((inn, pos) => {
          const out = flatOuters![pos];
          
          if (useComputeData && this.lastRawComputeRows) {
            // Look up from compute array using the outer and inner keys
            const computeItem = this.lastRawComputeRows.find((c: any) => 
              String(c[_doubleOuterKey || '']) === out && String(c[_doubleInnerKey || '']) === inn
            );
            if (computeItem) {
              const value = Number(computeItem[valKey]) || 0;
              if (pos === 0 || pos === 5) {
                console.log(`  [${pos}] Compute lookup inn="${inn}", out="${out}": ${valKey}=${value}`);
              }
              return value;
            }
            return 0;
          } else {
            // Aggregate from bar array
            const items = this.lastRawBarRows!.filter((r: any) => String(r[_doubleInnerKey || '']) === inn && String(r[_doubleOuterKey || '']) === out);
            return items.reduce((sum: number, it: any) => sum + (Number(it[valKey]) || 0), 0);
          }
        }),
        yAxisIndex: 1,
        smooth: false,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: { width: 2 },
        itemStyle: { color: preferredLineColor },
      }));
    } else {
      lineSeries =
        lineChartData?.series.map((s) => ({
          name: s.name,
          type: 'line',
          data: categories.map((cat) => s.data[lineChartData.category.indexOf(cat)] || 0),
          yAxisIndex: 1,
          smooth: false,
          symbol: 'circle',
          symbolSize: 8,
          lineStyle: { width: 1 },
          itemStyle: { color: preferredLineColor },
        })) || [];
    }

    console.log('barSeries:', barSeries);
    console.log('lineSeries:', lineSeries);

    // --- SLICE categories/series for visible window (clustered-chart behavior) ---
    this.maxVisibleCategories = this.data?.chartConfig?.maxVisibleCategories || this.maxVisibleCategories;
    const maxVis = Math.min(this.maxVisibleCategories, categories.length);
    const startIdx = Math.min(this.scrollOffset, Math.max(0, categories.length - maxVis));
    const endIdx = Math.min(startIdx + maxVis, categories.length);
    this.visibleCategories = categories.slice(startIdx, endIdx);
    const visibleBarSeries = barSeries.map((s: any) => ({ ...s, data: s.data.slice(startIdx, endIdx) }));
    const visibleLineSeries = lineSeries.map((s: any) => ({ ...s, data: s.data.slice(startIdx, endIdx) }));

    // Collect legend items using same color logic.
    // Legend should reflect visual stack order: topmost segment first.
    this.legendItems = [];
    for (let i = (barSeries || []).length - 1; i >= 0; i--) {
      const s = barSeries[i];
      this.legendItems.push({ name: s.name, color: s.itemStyle?.color });
    }
    lineSeries.forEach((s) => {
      const legendName = this.data?.lineLabel || s.name;
      this.legendItems.push({ name: legendName, color: s.itemStyle?.color || preferredLineColor });
    });

    // Re-apply active visual selection after data reload
    const _reapplySelection = () => {
      if (this.selectedCategories.length > 0) {
        this.highlightCategories(this.selectedCategories);
      } else if (this.selectedLegends.length > 0) {
        this.highlightSeriesByNames(this.selectedLegends);
      }
    };

    const option = {
      tooltip: {
        trigger: 'item',
        axisPointer: { type: 'shadow' },
        formatter: (param: any) => {
          const humanize = (s: any) => {
            if (s === null || s === undefined) return '';
            return String(s)
              .replace(/_/g, ' ')
              .split(' ')
              .map((w: string) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
              .join(' ');
          };

          const categoryLabel = this.data?.categoryLabel || humanize(this.data?.categoryKey || 'Category');
          let out = `${categoryLabel}: ${humanize(param.name)}<br/>`;

          if (param.seriesType === 'line') {
            const keyLabel =
              this.data?.lineLabel ||
              humanize(this.data?.lineSeriesName || this.data?.lineValueKey || param.seriesName || 'Value');
            const value = typeof param.value === 'number' ? `${Number(param.value).toFixed(2)}%` : param.value;
            out += `${keyLabel}: ${value}<br/>`;
          } else {
            const seriesLabel = this.data?.seriesLabel || humanize(this.data?.seriesKey || 'Series');
            out += `${seriesLabel}: ${humanize(param.seriesName)}<br/>`;
            const valueLabel = humanize(this.data?.valueKey || 'Value');
            const value = typeof param.value === 'number' ? formatScaled(param.value) : param.value;
            out += `${valueLabel}: ${value}<br/>`;

            // Show quantity if configured or present on the data object
            const quantityKey = this.data?.quantityKey || 'quantity';
            if (param.data !== null && param.data !== undefined) {
              if (quantityKey && param.data[quantityKey] !== undefined) {
                const qLabel = humanize(quantityKey);
                const qVal = formatQuantity(param.data[quantityKey]);
                out += `${qLabel}: ${qVal}<br/>`;
              } else if (typeof param.data === 'object') {
                for (const k of Object.keys(param.data)) {
                  if (k === 'value') continue;
                  const v = (param.data as any)[k];
                  const n = extractNumericForQty(v);
                  if (!Number.isNaN(n) && n !== 0) {
                    const qLabel = humanize(k);
                    const qVal = formatQuantity(v);
                    out += `${qLabel}: ${qVal}<br/>`;
                    break;
                  }
                }
              }
            }
          }

          return out;
        },
      },
      legend: {
        show: false, // Disable default legend
      },
      grid: {
        left: '20px',
        right: '20px',
        top: '30px',
        bottom: '40px',
        containLabel: true,
      },
      xAxis: (
        outerAxisHeaderLabels
          ? [
              {
                type: 'category',
                data: this.visibleCategories.length ? this.visibleCategories : categories,
                splitLine: { show: false },
                axisTick: { alignWithLabel: true },
                triggerEvent: true,
                axisLabel: {
                  interval: 0,
                  rotate: xLabelRotate,
                  formatter: (val: any) => (typeof val === 'string' ? val : String(val)),
                },
              },
              {
                type: 'category',
                data: this.visibleCategories.length ? outerAxisHeaderLabels.slice(startIdx, endIdx) : outerAxisHeaderLabels,
                position: 'bottom',
                offset: 30,
                axisLine: { show: false },
                axisTick: { show: false },
                triggerEvent: true,
                axisLabel: {
                  interval: 0,
                  fontWeight: 'bold',
                  fontSize: 11,
                },
              },
            ]
          : {
              type: 'category',
              data: this.visibleCategories.length ? this.visibleCategories : categories,
              splitLine: { show: false },
              axisTick: { alignWithLabel: true },
              triggerEvent: true,
              axisLabel: {
                // show every label (no auto-skip), allow optional rotation from chart config
                interval: 0,
                rotate: xLabelRotate,
                formatter: (val: any) => (typeof val === 'string' ? val : String(val)),
              },
            }
      ),
      yAxis: [
        {
          type: 'value',
          name: '',
          position: 'left',
          triggerEvent: true,
          splitLine: { show: false },
            axisLabel: {
            formatter: (value: number) => {
              return formatScaled(value);
            },
          },
        },
        {
          type: 'value',
          name: this.data?.rightAxisLabel || '',
          position: 'right',
          triggerEvent: true,
          splitLine: { show: false },
          axisLabel: {
            formatter: this.data?.rightAxisFormat || '{value}%',
          },
        },
      ],

      series: [...(typeof visibleBarSeries !== 'undefined' ? visibleBarSeries : barSeries), ...(typeof visibleLineSeries !== 'undefined' ? visibleLineSeries : lineSeries)],
     
    };

    console.log('Chart option:', option);
    try {
      this.myChart.setOption(option, true);
      this.originalOption = JSON.parse(JSON.stringify(option));
      this.myChart.hideLoading();
      // Ensure chart is resized to current container size
      setTimeout(() => this.myChart?.resize(), 0);
      // Only trigger a resize when the configured height changed in the JSON
      try {
        const currentHeight = String(this.data?.height || '');
        if (currentHeight !== this.lastAppliedHeight) {
          setTimeout(() => { try { this.myChart?.resize(); } catch (e) {} }, 0);
          this.lastAppliedHeight = currentHeight;
        }
      } catch (e) {}
      if (this.data?.filterKey || this.data?.enableLabelClick || this.data?.enableLegendClick) {
        this.setupChartClickEvent();
      }
      // Re-apply active visual selection after data reload
      _reapplySelection();
      this.updateArrowVisibility();
      setTimeout(() => this.updateArrowVisibility(), 300);
      console.log('Chart rendered successfully');
    } catch (error) {
      console.error('Error setting chart option:', error);
    }
  }

  // --- Horizontal scrollbar helpers ---
  getScrollbarThumbWidth(): number {
    const total = this.allCategories?.length || 0;
    if (total <= 0) return 0;
    const containerWidth = this.chartContainerRef?.nativeElement?.clientWidth || this.visibleWidth || 600;
    const ratio = Math.min(1, (this.visibleCategories.length || 0) / (total || 1));
    return Math.max(20, Math.round(containerWidth * ratio));
  }

  getScrollbarThumbLeft(): number {
    const containerWidth = this.chartContainerRef?.nativeElement?.clientWidth || this.visibleWidth || 600;
    const total = this.allCategories?.length || (this.visibleCategories.length || 0);
    const maxScroll = Math.max(0, total - this.maxVisibleCategories);
    if (maxScroll <= 0) return 0;
    const thumbWidth = this.getScrollbarThumbWidth();
    const maxThumbTravel = containerWidth - thumbWidth;
    return Math.floor((this.scrollOffset / maxScroll) * maxThumbTravel);
  }

  onScrollbarMouseDown(event: MouseEvent): void {
    event.preventDefault();
    const total = this.allCategories?.length || 0;
    if (total <= this.maxVisibleCategories) return;

    this.isScrollbarDragging = true;
    this.scrollbarStartX = event.clientX;
    this.scrollStartOffset = this.scrollOffset;

    this.dragHandler = (e: MouseEvent) => this.onScrollbarDrag(e);
    this.upHandler = () => this.onScrollbarMouseUp();
    document.addEventListener('mousemove', this.dragHandler!);
    document.addEventListener('mouseup', this.upHandler!);
  }

  protected onScrollbarDrag(event: MouseEvent): void {
    if (!this.isScrollbarDragging) return;
    const containerWidth = this.chartContainerRef?.nativeElement?.clientWidth || this.visibleWidth || 600;
    const total = this.allCategories?.length || 0;
    const maxScroll = Math.max(0, total - this.maxVisibleCategories);
    const thumbWidth = this.getScrollbarThumbWidth();
    const maxThumbTravel = containerWidth - thumbWidth;
    if (maxThumbTravel <= 0) return;

    const deltaX = event.clientX - this.scrollbarStartX;
    const startThumbLeft = (this.scrollStartOffset / maxScroll) * maxThumbTravel;
    const newThumbLeft = startThumbLeft + deltaX;
    const clampedLeft = Math.max(0, Math.min(newThumbLeft, maxThumbTravel));
    const newOffset = Math.floor((clampedLeft / maxThumbTravel) * maxScroll);
    try {
      console.debug('onScrollbarDrag', { deltaX, startThumbLeft, newThumbLeft, clampedLeft, newOffset, maxScroll, maxThumbTravel, containerWidth });
    } catch (e) {}
    if (newOffset !== this.scrollOffset && newOffset >= 0 && newOffset <= maxScroll) {
      this.scrollOffset = newOffset;
      // Re-render using the previously loaded datasets
      this.initChart(this.lastBarChartData, this.lastLineChartData);
    }
  }

  protected onScrollbarMouseUp(): void {
    this.isScrollbarDragging = false;
    if (this.dragHandler) {
      document.removeEventListener('mousemove', this.dragHandler);
      this.dragHandler = null;
    }
    if (this.upHandler) {
      document.removeEventListener('mouseup', this.upHandler);
      this.upHandler = null;
    }
    try { console.debug('onScrollbarMouseUp', { scrollOffset: this.scrollOffset }); } catch (e) {}
  }


   protected setupChartClickEvent(): void {
    this.myChart.off('click');
    this.myChart.on('click', (params: any) => {
      // Handle x-axis label clicks
      if (params.componentType === 'xAxis') {
        const axisIndex = params.xAxisIndex ?? params.axisIndex ?? 0;
        if (axisIndex === 1 && this.data?.enableDoubleXAxis && this.data?.enableXAxis2Click) {
          this.chartFilterHandler.handleDoubleXAxisClick(this.data, params.value);
        } else if (this.data?.enableLabelClick) {
          this.handleLabelClick(params.value);
        }
        return;
      }

      // Handle y-axis clicks
      if (params.componentType === 'yAxis') {
        const axisIndex = params.yAxisIndex ?? 0;
        if (axisIndex === 1 && this.data?.enableDoubleYAxis && this.data?.enableYAxis2Click) {
          this.chartFilterHandler.handleDoubleYAxisClick(this.data, params.value);
        }
        return;
      }

      // Skip line series clicks
      if (!params.seriesName || params.seriesType === 'line') return;

      // If enableLabelClick is on, treat bar clicks as label (category) clicks
      if (this.data?.enableLabelClick) {
        this.handleLabelClick(params.name);
        return;
      }

      const clickedSeries = params.seriesName;

      // Toggle: click same series again → reset
      if (this.selectedSeriesName === clickedSeries) {
        this.selectedSeriesName = null;
        this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
        return;
      }

      this.selectedSeriesName = clickedSeries;

      // Build values array matching filterKey order
      const filterKeys = (this.data.filterKey || '').split(',').map((k: string) => k.trim());
      const values: string[] = [];
      filterKeys.forEach((key: string) => {
        if (key === this.data.seriesKey) {
          values.push(clickedSeries);
        } else if (key === this.data.categoryKey) {
          values.push(params.name);
        }
      });

      this.chartFilterHandler.updateFilterSelection(this.data, values);
      this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
    });
  }

  protected highlightSeries(name: string): void {
    this.highlightSeriesByNames([name]);
  }

  protected resetHighlight(): void {
    this.resetSeriesHighlight();
  }
  protected transformBarData(
    rawData: any[],
    categoryKey: string,
    seriesKey: string,
    valueKey: string,
  ): { category: string[]; series: any[] } {
    console.log(
      'transformBarData called with rawData:',
      rawData,
      'keys:',
      categoryKey,
      seriesKey,
      valueKey,
    );
    const categories = [...new Set(rawData.map((d) => d[categoryKey]))].sort();
    const seriesNames = [...new Set(rawData.map((d) => d[seriesKey]))];
    const series = seriesNames.map((name) => {
      const data = categories.map((cat) => {
        const items = rawData.filter((d) => d[categoryKey] === cat && d[seriesKey] === name);
        return items.reduce((sum, item) => sum + (item[valueKey] || 0), 0);
      });
      return { name, data, type: 'bar' };
    });
    return { category: categories, series };
  }

  protected transformLineData(
    rawData: any[],
    categoryKey: string,
    valueKey: string,
  ): { category: string[]; series: any[] } {
    console.log('transformLineData called with rawData:', rawData, 'keys:', categoryKey, valueKey);
    const categories = [...new Set(rawData.map((d) => d[categoryKey]))].sort();
    const data = categories.map((cat) => {
      const item = rawData.find((d) => d[categoryKey] === cat);
      return item ? item[valueKey] || 0 : 0;
    });
    return { category: categories, series: [{ name: this.data?.lineSeriesName || valueKey, data, type: 'line' }] };
  }

  protected transformLineDataFromBar(
    rawBar: any[],
    categoryKey: string,
    valueKey: string,
    categoriesOrder?: string[],
  ): { category: string[]; series: any[] } {
    console.log('transformLineDataFromBar called with rawBar:', rawBar, 'keys:', categoryKey, valueKey, 'order:', categoriesOrder);
    const categories = categoriesOrder && categoriesOrder.length
      ? categoriesOrder
      : [...new Set(rawBar.map((d) => d[categoryKey]))].sort();
    const data = categories.map((cat) => {
      const items = rawBar.filter((d) => d[categoryKey] === cat && d[valueKey] != null);
      if (!items.length) return 0;
      const sum = items.reduce((s, it) => s + Number(it[valueKey] || 0), 0);
      return sum / items.length;
    });
    return { category: categories, series: [{ name: this.data?.lineSeriesName || valueKey, data, type: 'line' }] };
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  protected handleResize = (): void => {
    this.myChart?.resize();
  };

  // Legend pagination
  scrollLegend(direction: 'left' | 'right'): void {
    const element = this.legendContainerRef?.nativeElement;
    if (!element) return;

    const scrollAmount = element.clientWidth * 0.8;

    element.scrollBy({
      left: direction === 'right' ? scrollAmount : -scrollAmount,
      behavior: 'smooth'
    });
  }

  updateArrowVisibility(): void {
    if (!this.legendContainerRef) return;

    setTimeout(() => {
      const element = this.legendContainerRef.nativeElement;

      this.showLeftArrow = element.scrollLeft > 0;
      this.showRightArrow =
        element.scrollWidth - element.scrollLeft - element.clientWidth > 0;
    }, 0);
  }

  // ==================== CROSS-FILTERING METHODS ====================

  /**
   * Called when user clicks a legend item in the custom HTML legend.
   * Supports multi-select (toggle) when isMultiSelect is true.
   */
  onLegendItemClick(legendName: string): void {
    if (!this.data?.enableLegendClick) return;

    if (this.data?.isMultiSelect) {
      const idx = this.selectedLegends.indexOf(legendName);
      if (idx === -1) {
        this.selectedLegends = [...this.selectedLegends, legendName];
      } else {
        this.selectedLegends = this.selectedLegends.filter(l => l !== legendName);
      }
    } else {
      if (this.selectedLegends.length === 1 && this.selectedLegends[0] === legendName) {
        this.selectedLegends = [];
      } else {
        this.selectedLegends = [legendName];
      }
    }

    if (this.selectedLegends.length === 0) {
      this.broadcastLegendFilter([]);
    } else {
      this.broadcastLegendFilter(this.selectedLegends);
    }

    // Reload self with updated filters so the API call goes out
    this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
  }

  /**
   * Called when user clicks an x-axis label.
   * Supports multi-select (toggle) when isMultiSelect is true.
   */
  protected handleLabelClick(clickedCategory: string): void {
    if (!clickedCategory || !this.data?.enableLabelClick) return;
    const clickedStr = String(clickedCategory);

    if (this.data?.isMultiSelect) {
      const idx = this.selectedCategories.findIndex(c => String(c) === clickedStr);
      if (idx === -1) {
        this.selectedCategories = [...this.selectedCategories, clickedStr];
      } else {
        this.selectedCategories = this.selectedCategories.filter(c => String(c) !== clickedStr);
      }
    } else {
      if (this.selectedCategories.length === 1 && String(this.selectedCategories[0]) === clickedStr) {
        this.selectedCategories = [];
      } else {
        this.selectedCategories = [clickedStr];
      }
    }

    if (this.selectedCategories.length === 0) {
      this.broadcastCategoryFilter([]);
    } else {
      this.broadcastCategoryFilter(this.selectedCategories);
    }

    // Reload self with updated filters so the API call goes out
    this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
  }

  /**
   * Broadcast legend selection to listener charts.
   * 1) Emits legendSync for visual dimming on other charts
   * 2) Emits regular filter update so listener charts make API calls
   */
  protected broadcastLegendFilter(legends: string[]): void {
    const listenerConfig =
      this.data?.legendListener ||
      this.data?.legendListner ||
      this.data?.filterListener ||
      this.data?.filterListner;

    const legendKey = this.data?.legendKey || this.data?.filterKey;

    // Step 1: Emit legendSync for visual dimming on other charts
    const allSeriesNames = this.legendItems.map(l => l.name);
    const selectedIndices = legends.map(l => allSeriesNames.indexOf(l)).filter(i => i !== -1);
    const selectedColors = legends
      .map(l => this.legendItems.find(li => li.name === l)?.color || '')
      .filter(Boolean);
    const colorSyncEnabled = this.data?.chartConfig?.colorSyncEnabled === true;

    const legendSyncData = buildLegendSyncData(
      selectedIndices,
      allSeriesNames,
      this.data.widgetId,
      listenerConfig,
      { colorSyncEnabled, selectedColors, legendKey },
    );
    (this.globalFilterService as any).filterSubject.next(legendSyncData);

    // Step 2: Emit regular filter update so listener charts make API calls
    if (legendKey) {
      if (legends.length > 0) {
        this.globalFilterService.updateFilterSelection(
          legendKey, legends, false, this.data.widgetId, true, listenerConfig,
        );
      } else {
        this.globalFilterService.resetFilterSelection(legendKey, false, listenerConfig);
      }
    }
  }

  /**
   * Broadcast category (label) selection to listener charts.
   * 1) Emits legendSync for visual dimming on other charts
   * 2) Emits regular filter update so listener charts make API calls
   */
  protected broadcastCategoryFilter(categories: string[]): void {
    const listenerConfig =
      this.data?.labelListener ||
      this.data?.legendListener ||
      this.data?.filterListener ||
      this.data?.filterListner;

    const labelKey = this.data?.labelKey || this.data?.filterKey;

    // Step 1: Emit legendSync for visual dimming on other charts
    const legendSyncData = {
      ...this.globalFilterService.getCurrentFilters(),
      ...buildCategoryFilterData(categories, this.data.widgetId, listenerConfig, labelKey),
    };
    (this.globalFilterService as any).filterSubject.next(legendSyncData);

    // Step 2: Emit regular filter update so listener charts make API calls
    if (labelKey) {
      if (categories.length > 0) {
        this.globalFilterService.updateFilterSelection(
          labelKey, categories, false, this.data.widgetId, true, listenerConfig,
        );
      } else {
        this.globalFilterService.resetFilterSelection(labelKey, false, listenerConfig);
      }
    }
  }

  /**
   * Handle incoming legendSync from another chart (visual dimming).
   */
  protected handleExternalLegendSync(filters: any): void {
    if (filters.sourceWidgetId === this.data?.widgetId) return;
    if (!this.myChart) return;

    // Reset if empty selection
    if (filters.selectedLegends?.length === 0 && filters.selectedIndices?.length === 0) {
      this.selectedCategories = [];
      this.selectedLegends = [];
      this.resetCategoryHighlight();
      this.resetSeriesHighlight();
      return;
    }

    if (filters.isCategoryFilter && filters.selectedLegends?.length > 0) {
      // Category-based filter: match against x-axis labels
      const matchMode = (this.data?.chartConfig?.categoryMatchMode || 'contains') as MatchMode;
      const matchingCategories = this.allCategories.filter((cat: string) =>
        filters.selectedLegends.some((legend: string) => matchValue(cat, legend, matchMode))
      );
      if (matchingCategories.length > 0) {
        this.selectedCategories = matchingCategories;
        this.highlightCategories(matchingCategories);
      }
    } else if (filters.selectedLegends?.length > 0) {
      // Legend-based filter: try matching against series names first, then categories
      const seriesNames = this.legendItems.map(l => l.name);
      const matchMode = (this.data?.chartConfig?.categoryMatchMode || 'contains') as MatchMode;
      const matchingSeries = seriesNames.filter((name: string) =>
        filters.selectedLegends.some((legend: string) => matchValue(name, legend, matchMode))
      );
      if (matchingSeries.length > 0) {
        this.highlightSeriesByNames(matchingSeries);
      } else {
        // Try matching categories
        const matchingCategories = this.allCategories.filter((cat: string) =>
          filters.selectedLegends.some((legend: string) => matchValue(cat, legend, matchMode))
        );
        if (matchingCategories.length > 0) {
          this.highlightCategories(matchingCategories);
        }
      }
    }
  }

  // ==================== VISUAL HIGHLIGHTING ====================

  /**
   * Dim all bar series except those whose names are in the given list.
   */
  protected highlightSeriesByNames(names: string[]): void {
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      const seriesName = Array.isArray(s.name) ? s.name[0] : s.name;
      const seriesType = Array.isArray(s.type) ? s.type[0] : s.type;
      if (seriesType === 'bar') {
        return { ...s, itemStyle: { ...(Array.isArray(s.itemStyle) ? s.itemStyle[0] : s.itemStyle), opacity: names.includes(seriesName) ? 1 : 0.2 } };
      }
      return s;
    });

    this.myChart.setOption({ series: updatedSeries });
  }

  /**
   * Reset all bar series to full opacity.
   */
  protected resetSeriesHighlight(): void {
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      const seriesType = Array.isArray(s.type) ? s.type[0] : s.type;
      if (seriesType === 'bar') {
        return { ...s, itemStyle: { ...(Array.isArray(s.itemStyle) ? s.itemStyle[0] : s.itemStyle), opacity: 1 } };
      }
      return s;
    });

    this.myChart.setOption({ series: updatedSeries });
  }

  /**
   * Dim all categories except the selected ones.
   * Uses String() comparison so number 2016 matches string "2016".
   */
  protected highlightCategories(categories: string[]): void {
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    // Use String() comparison so number 2016 matches string "2016"
    const categoryIndices = categories
      .map((cat: string) => this.allCategories.findIndex(c => String(c) === String(cat)))
      .filter((i: number) => i !== -1);

    // Adjust indices for visible window
    const maxVis = Math.min(this.maxVisibleCategories, this.allCategories.length);
    const startIdx = Math.min(this.scrollOffset, Math.max(0, this.allCategories.length - maxVis));

    const updatedSeries = option.series.map((s: any) => {
      if (!s.data) return s;
      const newData = (Array.isArray(s.data[0]) ? s.data[0] : s.data).map((val: any, idx: number) => {
        const globalIdx = startIdx + idx;
        const isMatch = categoryIndices.includes(globalIdx);
        const opacity = isMatch ? 1 : 0.2;
        if (typeof val === 'object' && val !== null) {
          return { ...val, itemStyle: { ...(val.itemStyle || {}), opacity } };
        }
        return { value: val, itemStyle: { opacity } };
      });
      return { ...s, data: newData };
    });

    this.myChart.setOption({ series: updatedSeries });
  }

  /**
   * Reset all categories to full opacity.
   */
  protected resetCategoryHighlight(): void {
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      if (!s.data) return s;
      const newData = (Array.isArray(s.data[0]) ? s.data[0] : s.data).map((val: any) => {
        if (typeof val === 'object' && val !== null) {
          return { ...val, itemStyle: { ...(val.itemStyle || {}), opacity: 1 } };
        }
        return { value: val, itemStyle: { opacity: 1 } };
      });
      return { ...s, data: newData };
    });

    this.myChart.setOption({ series: updatedSeries });
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.handleResize);
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    this.filterSub?.unsubscribe();
    this.reflowSub?.unsubscribe();
    this.myChart?.dispose();
    this.resizeObserver?.disconnect();
  }
}

